<template>
  <div class="min-h-screen bg-bochas-bg px-4 py-6 sm:py-8">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8"
      >
        <h1 class="text-2xl sm:text-3xl font-bold text-white">
          {{ isEdit ? labels.title.edit : labels.title.new }}
        </h1>
        <router-link
          to="/admin/noticias"
          class="btn-bochas-trans text-sm self-start sm:self-auto"
        >
          ← Volver
        </router-link>
      </div>

      <form @submit.prevent="onSave" class="space-y-6">
        <!-- Título -->
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2"
            >Título *</label
          >
          <input
            v-model="form.title"
            type="text"
            required
            class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
            :placeholder="labels.placeholder"
          />
        </div>

        <!-- Descripción corta -->
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2"
            >Descripción corta</label
          >
          <input
            v-model="form.description"
            type="text"
            maxlength="500"
            class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
            placeholder="Una línea que resume la nota"
          />
        </div>

        <!-- Galería de imágenes -->
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2">
            Galería de imágenes
          </label>

          <!-- Dropzone -->
          <div
            @dragover.prevent="dragOver = true"
            @dragleave="dragOver = false"
            @drop.prevent="onDrop"
            :class="[
              'border-2 border-dashed rounded-lg p-4 sm:p-6 text-center transition-colors cursor-pointer',
              dragOver
                ? 'border-bochas-rojo bg-bochas-rojo/10'
                : 'border-gray-700 bg-white/5 hover:border-gray-500',
            ]"
            @click="$refs.fileInput.click()"
          >
            <input
              ref="fileInput"
              type="file"
              multiple
              accept="image/*"
              class="hidden"
              @change="onFileSelect"
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="mx-auto w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
              />
            </svg>
            <p class="text-sm text-gray-300">
              <span class="font-semibold text-bochas-rojo"
                >Tocá para subir</span
              >
              <span class="hidden sm:inline"> o arrastrá imágenes</span>
            </p>
            <p class="text-xs text-gray-500 mt-1">
              PNG, JPG o WEBP · Máx. 10 imágenes · Se suben al guardar
            </p>
          </div>

          <!-- Previews -->
          <div
            v-if="imágenes.length > 0"
            class="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
          >
            <div
              v-for="(img, idx) in imágenes"
              :key="img.id"
              class="relative group rounded-lg overflow-hidden border-2 aspect-square"
              :class="img.is_cover ? 'border-bochas-rojo' : 'border-gray-700'"
            >
              <img
                :src="img.url"
                :alt="img.originalName || `Imagen ${idx + 1}`"
                class="w-full h-full object-cover"
              />

              <!-- Badge portada -->
              <div
                v-if="img.is_cover"
                class="absolute top-1 left-1 bg-bochas-rojo text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wide"
              >
                Portada
              </div>

              <!-- Badge pendiente (aún no subido) -->
              <div
                v-if="img.status === 'pending'"
                class="absolute top-1 right-1 bg-yellow-500/90 text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wide"
              >
                Pendiente
              </div>

              <!-- Acciones -->
              <div
                class="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 sm:gap-2"
              >
                <button
                  v-if="!img.is_cover"
                  type="button"
                  @click.stop="setCover(idx)"
                  class="px-2 py-1 sm:px-3 sm:py-1.5 bg-bochas-rojo text-white text-xs sm:text-sm rounded font-medium hover:bg-bochas-rojo/80"
                  title="Marcar cómo portada"
                >
                  Portada
                </button>
                <button
                  v-if="idx > 0"
                  type="button"
                  @click.stop="moveImage(idx, -1)"
                  class="px-2 py-1 sm:px-3 sm:py-1.5 bg-white/20 text-white text-xs sm:text-sm rounded hover:bg-white/30"
                  title="Mover a la izquierda"
                >
                  ←
                </button>
                <button
                  v-if="idx < imágenes.length - 1"
                  type="button"
                  @click.stop="moveImage(idx, 1)"
                  class="px-2 py-1 sm:px-3 sm:py-1.5 bg-white/20 text-white text-xs sm:text-sm rounded hover:bg-white/30"
                  title="Mover a la derecha"
                >
                  →
                </button>
                <button
                  type="button"
                  @click.stop="removeImage(idx)"
                  class="px-2 py-1 sm:px-3 sm:py-1.5 bg-bochas-rojo text-white text-xs sm:text-sm rounded hover:bg-red-700"
                  title="Eliminar"
                >
                  ✕
                </button>
              </div>

              <!-- Subiendo (en onSave) -->
              <div
                v-if="img.status === 'uploading'"
                class="absolute inset-0 bg-black/70 flex items-center justify-center"
              >
                <div
                  class="w-6 h-6 sm:w-8 sm:h-8 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin"
                ></div>
              </div>
            </div>
          </div>

          <p v-if="uploadError" class="text-xs text-bochas-rojo mt-2">
            {{ uploadError }}
          </p>
        </div>

        <!-- Tag -->
        <div v-if="tags.length > 0">
          <label class="block text-sm font-medium text-gray-300 mb-2"
            >Categoría</label
          >
          <select
            v-model="form.tag_id"
            class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white focus:outline-none focus:border-bochas-rojo transition-colors"
          >
            <option :value="null">Sin categoría</option>
            <option v-for="tag in tags" :key="tag.id" :value="tag.id">
              {{ tag.name }}
            </option>
          </select>
        </div>

        <!-- Contenido WYSIWYG -->
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2">
            Contenido *
          </label>
          <div class="bg-white rounded-lg overflow-hidden">
            <div ref="quillEditor"></div>
          </div>
          <p class="text-xs text-gray-500 mt-2">
            Editor enriquecido: negrita, itálica, títulos, listas, links,
            imágenes.
          </p>
        </div>

        <!-- Estado -->
        <div class="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.status"
              type="radio"
              value="draft"
              class="text-bochas-rojo focus:ring-bochas-rojo"
            />
            <span class="text-gray-300 text-sm">Guardar cómo borrador</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.status"
              type="radio"
              value="published"
              class="text-bochas-rojo focus:ring-bochas-rojo"
            />
            <span class="text-gray-300 text-sm">Publicar ahora</span>
          </label>
        </div>

        <!-- Error -->
        <div
          v-if="error"
          class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 text-sm rounded-lg px-4 py-3"
        >
          {{ error }}
        </div>

        <!-- Submit -->
        <div class="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            :disabled="saving || uploading"
            class="btn-bochas-rojo"
            :class="{ 'opacity-60 cursor-not-allowed': saving || uploading }"
          >
            <span v-if="uploading">Subiendo imágenes...</span>
            <span v-else-if="saving">Guardando...</span>
            <span v-else>{{
              isEdit ? labels.submitEdit : labels.submitNew
            }}</span>
          </button>
          <router-link
            :to="listPath"
            class="btn-bochas-trans text-center"
          >
            Cancelar
          </router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import Quill from "quill";
import {
  crearNoticia,
  editarNoticia,
  obtenerNoticiaAdmin,
} from "@/services/noticias/noticiasService";
import { listarTags } from "@/services/tags/tagsService";
import { useConfirm } from "@/composables/useConfirm";
import {
  subirImagenesNoticia,
  eliminarArchivo,
} from "@/services/files/filesService";
import { sanitizeNewsContent } from "@/utils/newsContent";

const route = useRoute();
const router = useRouter();
const { confirm } = useConfirm();

// El editor sirve para 3 tipos de contenido: NOTICIA, HISTORIA, NOTA.
// El tipo lo define la ruta via meta.contentType (definido en router/index.js).
const contentType = computed(() => route.meta?.contentType || "NOTICIA");
const listPath = computed(() => {
  if (contentType.value === "HISTORIA") return "/admin/historias";
  if (contentType.value === "NOTA") return "/admin/notas";
  return "/admin/noticias";
});
const labels = computed(() => {
  if (contentType.value === "HISTORIA") {
    return {
      singular: "historia",
      title: { new: "Nueva historia", edit: "Editar historia" },
      submitNew: "Crear historia",
      submitEdit: "Guardar cambios",
      placeholder: "Ej: La primera bocha en Bochas Club",
      quill: "Escribí tu historia acá...",
      alertImages: "Máximo 10 imágenes por historia.",
      errorLoad: "No se pudo cargar la historia.",
      errorSave: "No se pudo guardar la historia.",
      errorPartial: "La historia se guardó, pero hubo un error al subir las imágenes. ",
    };
  }
  if (contentType.value === "NOTA") {
    return {
      singular: "nota",
      title: { new: "Nueva nota", edit: "Editar nota" },
      submitNew: "Crear nota",
      submitEdit: "Guardar cambios",
      placeholder: "Ej: Una reflexión sobre la temporada",
      quill: "Escribí tu nota acá...",
      alertImages: "Máximo 10 imágenes por nota.",
      errorLoad: "No se pudo cargar la nota.",
      errorSave: "No se pudo guardar la nota.",
      errorPartial: "La nota se guardó, pero hubo un error al subir las imágenes. ",
    };
  }
  return {
    singular: "noticia",
    title: { new: "Nueva noticia", edit: "Editar noticia" },
    submitNew: "Crear noticia",
    submitEdit: "Guardar cambios",
    placeholder: "Ej: Torneo de Parejas en Río Segundo",
    quill: "Escribí tu noticia acá...",
    alertImages: "Máximo 10 imágenes por noticia.",
    errorLoad: "No se pudo cargar la noticia.",
    errorSave: "No se pudo guardar la noticia.",
    errorPartial: "La noticia se guardó, pero hubo un error al subir las imágenes. ",
  };
});

const form = reactive({
  title: "",
  description: "",
  content: "",
  tag_id: null,
  status: "draft",
  type: contentType.value,
});

const tags = ref([]);
const saving = ref(false);
const uploading = ref(false);
const error = ref("");
const uploadError = ref("");
const dragOver = ref(false);

// Estado local de la galería.
// Cada item:
//   { id, url, originalName, file?, is_cover, status: 'uploaded'|'pending'|'uploading', newsImageId?, fileId? }
// - 'file' (File object) solo para items 'pending' o 'uploading'
// - 'newsImageId'/'fileId' solo para items 'uploaded'
const imágenes = ref([]);

const quillEditor = ref(null);
let quill = null;

const editorFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "align",
  "blockquote",
  "code-block",
  "link",
  "image",
];

function removeExternalTextStyles(_node, delta) {
  delta.ops.forEach((op) => {
    if (!op.attributes) return;
    delete op.attributes.font;
    delete op.attributes.size;
    delete op.attributes.color;
    delete op.attributes.background;
    if (!Object.keys(op.attributes).length) delete op.attributes;
  });
  return delta;
}

const isEdit = computed(() => !!route.params.id);

// -------------------- Quill --------------------
function initQuill(initialContent = "") {
  if (!quillEditor.value) return;
  quill = new Quill(quillEditor.value, {
    theme: "snow",
    placeholder: labels.value.quill,
    formats: editorFormats,
    modules: {
      toolbar: [
        [{ header: [2, 3, 4, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["blockquote", "code-block"],
        ["link", "image"],
        ["clean"],
      ],
      clipboard: {
        matchVisual: false,
        matchers: [[window.Node.ELEMENT_NODE, removeExternalTextStyles]],
      },
    },
  });
  if (initialContent) {
    quill.clipboard.dangerouslyPasteHTML(initialContent);
  }
  quill.on("text-change", () => {
    form.content = quill.root.innerHTML;
  });
}

function setQuillContent(html) {
  if (quill && html) {
    quill.clipboard.dangerouslyPasteHTML(sanitizeNewsContent(html));
    form.content = quill.root.innerHTML;
  }
}

// -------------------- Tags --------------------
async function loadTags() {
  try {
    tags.value = await listarTags();
  } catch {
    tags.value = [];
  }
}

// -------------------- Cargar noticia (edición) --------------------
async function loadNoticia() {
  if (!isEdit.value) return;
  try {
    const data = await obtenerNoticiaAdmin(route.params.id);
    Object.assign(form, {
      title: data.title || "",
      description: data.description || "",
      content: data.content || "",
      tag_id: data.tag_id || null,
      status: data.status || "draft",
      type: data.type || "NOTICIA",
    });
    // Cargar galería existente (ya está subida)
    if (data.images && Array.isArray(data.images)) {
      imágenes.value = data.images
        .map((img) => ({
          id: `existing-${img.id}`,
          newsImageId: img.id,
          url: img.url,
          originalName: "",
          is_cover: !!img.is_cover,
          status: "uploaded",
        }))
        .sort((a, b) => {
          // Portada primero, después por 'order' (no está acá, pero mantenemos orden del backend)
          if (a.is_cover && !b.is_cover) return -1;
          if (!a.is_cover && b.is_cover) return 1;
          return 0;
        });
    }
    // Quill se inicializa después con el contenido
    setTimeout(() => setQuillContent(data.content || ""), 50);
  } catch (err) {
    error.value = labels.value.errorLoad;
  }
}

// -------------------- Selección de imágenes (preview local) --------------------
// Antes: las imágenes se subían a S3 al arrastrarlas (sin newsId).
// Ahora: se previsualizan localmente con URL.createObjectURL y se suben
// en onSave() con el newsId de la noticia ya creada/editada.
function onFilesSelected(fileList) {
  if (!fileList || fileList.length === 0) return;
  const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
  if (files.length === 0) {
    uploadError.value = "Solo se permiten imágenes.";
    return;
  }
  if (imágenes.value.length + files.length > 10) {
    uploadError.value = labels.value.alertImages;
    return;
  }
  uploadError.value = "";

  const hayPortada = imágenes.value.some((i) => i.is_cover);
  const nuevos = files.map((f) => ({
    id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    file: f,
    url: URL.createObjectURL(f),
    originalName: f.name,
    is_cover: false,
    status: "pending",
  }));
  // Si no hay portada todavía, la primera nueva es portada
  if (!hayPortada && nuevos.length > 0) nuevos[0].is_cover = true;
  imágenes.value.push(...nuevos);
}

function onFileSelect(e) {
  onFilesSelected(e.target.files);
  e.target.value = ""; // permite reseleccionar el mismo archivo
}

function onDrop(e) {
  dragOver.value = false;
  onFilesSelected(e.dataTransfer.files);
}

// -------------------- Acciones sobre la galería --------------------
function setCover(idx) {
  imágenes.value.forEach((img, i) => {
    img.is_cover = i === idx;
  });
}

function moveImage(idx, dir) {
  const newIdx = idx + dir;
  if (newIdx < 0 || newIdx >= imágenes.value.length) return;
  const tmp = imágenes.value[idx];
  imágenes.value[idx] = imágenes.value[newIdx];
  imágenes.value[newIdx] = tmp;
}

async function removeImage(idx) {
  const img = imágenes.value[idx];
  const confirmed = await confirm({
    title: "Eliminar imagen",
    message: "¿Eliminar está imagen?",
  });
  if (!confirmed) return;

  // Si ya estaba subida (caso edición), borrarla del backend
  if (img.status === "uploaded" && (img.fileId || img.newsImageId)) {
    try {
      await eliminarArchivo({
        fileUrl: img.url,
        newsImageId: img.newsImageId,
      });
    } catch (err) {
      // Si falla el borrado en backend, igual la quitamos del UI
      console.warn("No se pudo borrar del backend:", err);
    }
  }
  // Liberar object URL si era local
  if (img.url && img.url.startsWith("blob:")) {
    URL.revokeObjectURL(img.url);
  }
  imágenes.value.splice(idx, 1);
}

// -------------------- Guardar (2 pasos) --------------------
// Paso 1: crear/actualizar la noticia sin imágenes
// Paso 2: con el newsId, subir las imágenes pendientes (el backend crea
//         las filas en news_image automáticamente)
async function uploadPendingImages(newsId) {
  const pending = imágenes.value.filter(
    (i) => i.status === "pending" && i.file,
  );
  if (pending.length === 0) return;

  uploading.value = true;
  // Marcar cómo uploading (UX)
  pending.forEach((p) => (p.status = "uploading"));

  try {
    // El backend pone is_cover=true a la PRIMERA imagen del array.
    // Si el usuario marcó otra cómo portada, la movemos al principio.
    const sortedPending = [...pending].sort((a, b) => {
      if (a.is_cover && !b.is_cover) return -1;
      if (!a.is_cover && b.is_cover) return 1;
      return 0;
    });
    const filesToUpload = sortedPending.map((p) => p.file);
    const res = await subirImagenesNoticia(filesToUpload, { newsId });

    // Mapear respuestas a los items correspondientes
    res.items.forEach((item, idx) => {
      const placeholder = sortedPending[idx];
      if (!placeholder) return;
      const realIdx = imágenes.value.findIndex((i) => i.id === placeholder.id);
      if (realIdx === -1) return;
      imágenes.value[realIdx] = {
        id: `uploaded-${item.fileId}`,
        fileId: item.fileId,
        newsImageId: item.newsImageId,
        url: item.url,
        originalName: item.originalName,
        is_cover: placeholder.is_cover,
        status: "uploaded",
      };
    });
  } catch (err) {
    // Revertir 'uploading' a 'pending' para reintento
    pending.forEach((p) => (p.status = "pending"));
    throw err;
  } finally {
    uploading.value = false;
  }
}

async function onSave() {
  saving.value = true;
  error.value = "";

  try {
    form.content = sanitizeNewsContent(form.content);
    // Paso 1: crear/actualizar la noticia (sin imágenes, sin pendingImageIds)
    const payload = {
      title: form.title,
      description: form.description || null,
      content: form.content,
      tag_id: form.tag_id || null,
      status: form.status,
      type: form.type,
    };

    let newsId;
    if (isEdit.value) {
      await editarNoticia(route.params.id, payload);
      newsId = Number(route.params.id);
    } else {
      const created = await crearNoticia(payload);
      newsId = created.id;
    }

    // Paso 2: subir imágenes pendientes con el newsId
    if (newsId) {
      try {
        await uploadPendingImages(newsId);
      } catch (err) {
        // La noticia ya está guardada. Mostrar error y dejar al usuario
        // en la pantalla para que pueda reintentar.
        error.value =
          labels.value.errorPartial +
          (err.response?.data?.message || err.message || "Probá de nuevo.");
        return;
      }
    }

    router.push(listPath.value);
  } catch (err) {
    error.value =
      err.response?.data?.message || labels.value.errorSave;
  } finally {
    saving.value = false;
  }
}

// -------------------- Lifecycle --------------------
onMounted(async () => {
  await loadTags();
  // Quill primero (necesita el ref), después carga contenido
  initQuill("");
  await loadNoticia();
});

onBeforeUnmount(() => {
  // Liberar object URLs
  imágenes.value.forEach((img) => {
    if (img.url && img.url.startsWith("blob:")) URL.revokeObjectURL(img.url);
  });
});
</script>

<style scoped>
:deep(.ql-toolbar) {
  border-color: #374151 !important;
  background: #1f2937;
}
:deep(.ql-toolbar .ql-stroke) {
  stroke: #d1d5db !important;
}
:deep(.ql-toolbar .ql-fill) {
  fill: #d1d5db !important;
}
:deep(.ql-toolbar .ql-picker-label) {
  color: #d1d5db !important;
}
:deep(.ql-toolbar button:hover .ql-stroke),
:deep(.ql-toolbar button.ql-active .ql-stroke) {
  stroke: #a52121 !important;
}
:deep(.ql-toolbar button:hover .ql-fill),
:deep(.ql-toolbar button.ql-active .ql-fill) {
  fill: #a52121 !important;
}
:deep(.ql-container) {
  border-color: #374151 !important;
  background: white;
  min-height: 240px;
  font-size: 1rem;
  color: #111;
}
:deep(.ql-editor) {
  min-height: 240px;
}
:deep(.ql-editor.ql-blank::before) {
  color: #9ca3af;
  font-style: normal;
}
</style>
